import { cacheKeys } from "@constants/cache-keys";
import { VERSION_CHECKER_TEXTS } from "@constants/version-checker";
import { buildType } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { ENDPOINT_APP_CONFIG, isDev } from "@memorang/constants/urls";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { type ReactNode, createContext, useContext, useState } from "react";
import { Card, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const parseVersion = (version: string): number[] => {
	return version.split(".").map((part) => Number.parseInt(part, 10) || 0);
};

const isVersionGreater = (remote?: string, current?: string) => {
	if (!(remote && current)) {
		return false;
	}
	const remoteParts = parseVersion(remote);
	const currentParts = parseVersion(current);

	for (let i = 0; i < Math.max(remoteParts.length, currentParts.length); i++) {
		const remotePart = remoteParts[i] ?? 0;
		const currentPart = currentParts[i] ?? 0;

		if (remotePart > currentPart) {
			return true;
		}
		if (remotePart < currentPart) {
			return false;
		}
	}

	return false;
};

type VersionCheckerContextType = {
	isNewVersionAvailable: boolean;
	currentVersion: string;
	remoteVersion?: string;
	dismissBanner: () => void;
	refreshApp: () => void;
	isDismissed: boolean;
};

const VersionCheckerContext = createContext<VersionCheckerContextType | null>(
	null,
);

const useVersionChecker = () => {
	const context = useContext(VersionCheckerContext);
	if (!context) {
		throw new Error(
			"useVersionChecker must be used within VersionCheckerProvider",
		);
	}
	return context;
};

type VersionCheckerProviderProps = {
	children: ReactNode;
};

const RefreshBanner = () => {
	const { dismissBanner, refreshApp } = useVersionChecker();
	const insets = useSafeAreaInsets();

	return (
		<Box
			position="fixed"
			bottom={0}
			right={0}
			zIndex={1000}
			paddingRight={16}
			paddingBottom={Math.max(insets.bottom, 16)}
			backgroundColor="transparent"
			// @ts-ignore
			role="banner"
		>
			<Card elevation={4}>
				<Card.Content
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 16,
					}}
				>
					<Text variant="bodyMedium">
						{VERSION_CHECKER_TEXTS.NEW_VERSION_AVAILABLE}
					</Text>

					<Box
						flexDirection="row"
						alignItems="center"
						gap={8}
						justifyContent="flex-end"
					>
						<Button
							onPress={dismissBanner}
							accessibilityLabel={
								VERSION_CHECKER_TEXTS.DISMISS_ACCESSIBILITY_LABEL
							}
						>
							{VERSION_CHECKER_TEXTS.DISMISS}
						</Button>
						<Button
							mode="contained"
							onPress={refreshApp}
							accessibilityLabel={
								VERSION_CHECKER_TEXTS.REFRESH_ACCESSIBILITY_LABEL
							}
						>
							{VERSION_CHECKER_TEXTS.REFRESH}
						</Button>
					</Box>
				</Card.Content>
			</Card>
		</Box>
	);
};

const VersionCheckerProvider = ({ children }: VersionCheckerProviderProps) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const currentVersion = useAppStore((state) => state.app.version) || "0.0.0";

	const { data: remoteVersion } = useQuery({
		queryKey: [cacheKeys.versionChecker, currentVersion, buildType],
		queryFn: async (): Promise<string | undefined> => {
			try {
				const { data } = await axios.get(`${ENDPOINT_APP_CONFIG}/${buildType}`);
				const version = data?.app?.version;
				return typeof version === "string" ? version : undefined;
			} catch {
				return undefined;
			}
		},
		enabled: isWeb && !!buildType,
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchOnMount: true,
		refetchInterval: 60000,
		retry: false,
	});

	const isNewVersionAvailable = !!(
		isWeb &&
		remoteVersion &&
		isVersionGreater(remoteVersion, currentVersion) &&
		!isDismissed &&
		!isDev
	);

	const dismissBanner = () => {
		setIsDismissed(true);
	};

	const refreshApp = () => {
		window.location.reload();
	};

	const contextValue: VersionCheckerContextType = {
		isNewVersionAvailable,
		currentVersion,
		remoteVersion,
		dismissBanner,
		refreshApp,
		isDismissed,
	};

	return (
		<VersionCheckerContext.Provider value={contextValue}>
			{children}
			{isNewVersionAvailable && <RefreshBanner />}
		</VersionCheckerContext.Provider>
	);
};

export default VersionCheckerProvider;
